"use client";

import { Typography } from "@/components/shared/typography/typography";
import { GOOGLE_RATING } from "@/lib/constants";
import { cn } from "@/lib/utils";
import {
  Leaf,
  HandHeart,
  NotebookPen,
  TicketCheck,
  Headset,
} from "lucide-react";

export default function Beliefs({
  className,
  googleRating = GOOGLE_RATING,
}: {
  className?: string;
  googleRating?: number;
}) {
  const features = [
    {
      id: 1,
      icon: TicketCheck,
      title: "Seamless visa processing",
      description:
        "Quick, hassle-free visa assistance for a smooth travel start.",
    },
    {
      id: 2,
      icon: NotebookPen,
      title: "Curated by experts",
      description:
        "Expert-designed itineraries ensuring unique, memorable travel experiences.",
    },
    {
      id: 3,
      icon: Headset,
      title: "Prompt on-trip Support",
      description:
        "Round-the-clock assistance to keep your journey smooth, wherever you are.",
    },
    {
      id: 4,
      icon: HandHeart,
      title: "Exclusive Deals",
      description:
        "Special offers, seasonal promotions, and loyalty rewards to make every trip more rewarding.",
    },
    {
      id: 5,
      icon: (props: React.SVGProps<SVGSVGElement>) => (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-user-star-icon lucide-user-star"
          {...props}>
          <path d="M16.051 12.616a1 1 0 0 1 1.909.024l.737 1.452a1 1 0 0 0 .737.535l1.634.256a1 1 0 0 1 .588 1.806l-1.172 1.168a1 1 0 0 0-.282.866l.259 1.613a1 1 0 0 1-1.541 1.134l-1.465-.75a1 1 0 0 0-.912 0l-1.465.75a1 1 0 0 1-1.539-1.133l.258-1.613a1 1 0 0 0-.282-.866l-1.156-1.153a1 1 0 0 1 .572-1.822l1.633-.256a1 1 0 0 0 .737-.535z" />
          <path d="M8 15H7a4 4 0 0 0-4 4v2" />
          <circle cx="10" cy="7" r="4" />
        </svg>
      ),
      title: `Rated ${googleRating} out of 5`,
      description: "Rave reviews on online platforms from our happy customers.",
    },
    {
      id: 6,
      icon: Leaf,
      title: "Sustainable Travel",
      description:
        "Environment friendly experience in collaboration with local communities.",
    },
  ];
  return (
    <div className={cn("bg-gray-blue-50 py-8 md:py-12", className)}>
      <div className="app-container">
        <div className="mx-auto mb-6 md:mb-12">
          <Typography
            variant="h3"
            as="h3"
            className="md:text-display-sm text-xl font-medium text-gray-900">
            What makes us different
          </Typography>
        </div>

        <div className="font-family-display grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-12">
          {features.map((feature) => (
            <div key={feature.id} className="text-left">
              <div className="border-brand-100 mb-3 flex h-10 w-10 items-center justify-center rounded-full border-[1px] md:mb-6 md:h-16 md:w-16">
                <feature.icon className="scale-100 text-xl text-pink-600 md:scale-140 md:text-2xl" />
              </div>
              <Typography
                variant="p"
                as="p"
                className="text-md mb-2 leading-snug font-semibold text-gray-900 md:mb-4 md:text-xl">
                {feature.title}
              </Typography>
              <Typography
                variant="p-sm"
                as="p"
                className="text-xs leading-relaxed text-balance text-gray-600 md:pr-16 md:text-sm">
                {feature.description}
              </Typography>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
